import {
    IonButton,
    IonCol,
    IonIcon,
    IonLabel,
    IonRow,
    IonText,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { compare } from 'compare-versions';
import { refresh } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { IAssetStateData } from '../../interfaces/IAssetState';
import {
    accessTokenAtom,
    enableFastModeRequest,
    jobIdAtom,
    setTargetTemperatureRequest,
    testsPassedAtom,
    updateDBStateAtom,
} from '../../store/Atoms';
import {
    devicesStateRequest,
    trackActionsRequest,
    triggerRequest,
} from '../../store/Requests';
import { THomeAssetDataWithFWAndCalibration } from '../../types/HomeAssetData';
import {
    CONSUMPTION_TEST_MAX_THRESHOLD,
    CONSUMPTION_TEST_MIN_THRESHOLD,
    MAX_TEMP,
    MIN_TEMP,
    POLLING_MAX_TICK_COUNT,
    POLLING_SELF_TEST_TIMEOUT,
    POLLING_TIMEOUT,
} from '../../utils/constants';

enum CommandType {
    MAX = 'max',
    MIN = 'min',
    SELF = 'self',
}

/** The result of a self-test */
enum SelfTestResult {
    /** Test has not been run */
    SELF_TEST_RESULT_OFF = 0,
    /** Test is currently running */
    SELF_TEST_RESULT_RUNNING = 1,
    /** Heater did not start heating in time or did not stop heating */
    SELF_TEST_RESULT_ERR_SWITCH = 2,
    /** Heater power consumption was not maintained long enough */
    SELF_TEST_RESULT_ERR_KEEP = 3,
    /** Heater behaved correctly */
    SELF_TEST_RESULT_SUCCESS = 4,
}

interface IAssetTestProps {
    device: THomeAssetDataWithFWAndCalibration;
}

const DEFAULT_ASSET_STATE: IAssetStateData = {
    consumption: 0,
    emergencyMode: 0,
    lastAverageTemperature: 0,
    lastTargetTemperature: 0,
    lastInternalTemperature: 0,
    online: false,
    relayState: 0,
    receivedAt: 0,
    firmwareVersion: '',
    ts: 0,
    calibrationState: 0,
    lastEvents: {
        lastSelfTestResult: {
            result: 0,
            timestamp: 0,
        },
    },
};

const MIN_FW_VERSION_SELF_TEST = '1.6.1';

const AssetTest: React.FC<IAssetTestProps> = ({ device }) => {
    const { homeAssetId, isNew } = device;
    const timeoutId = useRef<ReturnType<typeof setTimeout>>();
    const [presentToast] = useIonToast();
    const [jobId] = useAtom(jobIdAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [testsPassed, setTestsPassed] = useAtom(testsPassedAtom);
    const [testsFailed, setTestsFailed] = useState<{
        [key in CommandType]?: boolean | {};
    }>({});

    const testsPassedCount =
        testsPassed && homeAssetId in testsPassed
            ? Object.entries(testsPassed[homeAssetId]).length
            : 0;

    const minTestPassed = !!(
        testsPassedCount && testsPassed[homeAssetId][CommandType.MIN]
    );

    const maxTestPassed = !!(
        testsPassedCount && testsPassed[homeAssetId][CommandType.MAX]
    );

    const selfTestPassed = !!(
        testsPassedCount && testsPassed[homeAssetId][CommandType.SELF]
    );
    const selfTestFailed = !!(
        Object.entries(testsFailed).length && testsFailed[CommandType.SELF]
    );

    const [commandType, setCommandType] = useState<CommandType | null>(
        maxTestPassed ? CommandType.MIN : CommandType.MAX
    );
    const [showTestDetails, setShowTestDetails] = useState(false);
    const [temperatureValue, setTemperatureValue] = useState(0);
    const [, setTickCount] = useState(1);
    const [testInProgress, setTestInProgress] = useState(false);
    const [assetStateData, setAssetStateData] =
        useState<IAssetStateData>(DEFAULT_ASSET_STATE);
    const useSelfTest = compare(
        device.firmwareVersion,
        MIN_FW_VERSION_SELF_TEST,
        '>='
    );
    const selfTestStartTimestampRef = useRef<number>(0);

    const setTargetTemperature = useCallback(
        (temperature: number) => {
            if (homeAssetId !== '') {
                return Promise.all([
                    setTargetTemperatureRequest(
                        accessToken,
                        jobId,
                        homeAssetId,
                        temperature
                    ),
                    enableFastModeRequest(accessToken, jobId),
                ]);
            }
        },
        [accessToken, homeAssetId, jobId]
    );

    const stopPolling = useCallback(() => {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
        selfTestStartTimestampRef.current = 0;
        setTestInProgress(false);
    }, []);

    const startTestPolling = useCallback(
        (command: CommandType) => {
            timeoutId.current = setTimeout(
                async () => {
                    try {
                        const [homeId, relativeAssetId] =
                            homeAssetId.split('/');
                        const data = await devicesStateRequest(
                            accessToken,
                            jobId,
                            +homeId,
                            [+relativeAssetId]
                        );
                        const assetStateData = Object.entries(
                            data[homeAssetId]
                        ).length
                            ? data[homeAssetId]
                            : DEFAULT_ASSET_STATE;

                        setAssetStateData(assetStateData);

                        if (command === CommandType.SELF) {
                            if (
                                assetStateData.online &&
                                assetStateData.lastEvents.lastSelfTestResult
                                    .timestamp >=
                                    selfTestStartTimestampRef.current &&
                                assetStateData.lastEvents.lastSelfTestResult
                                    .result ===
                                    SelfTestResult.SELF_TEST_RESULT_SUCCESS
                            ) {
                                stopPolling();
                                setTestsPassed((testPassed) => ({
                                    ...testPassed,
                                    [homeAssetId]: {
                                        ...testPassed[homeAssetId],
                                        [command]: true,
                                    },
                                }));
                                await updateDBState();
                                return;
                            }
                        }

                        // If the operation is set to min
                        // Check threshold and return failed if the power is bigger than the threshold
                        // Else we wait for the timer to finish and if for the whole
                        // Period the power did not exceed the threshold we finish with a success
                        if (command === CommandType.MIN) {
                            if (
                                assetStateData.online &&
                                assetStateData.consumption <
                                    CONSUMPTION_TEST_MIN_THRESHOLD
                            ) {
                                stopPolling();
                                setTestsPassed((testPassed) => ({
                                    ...testPassed,
                                    [homeAssetId]: {
                                        ...testPassed[homeAssetId],
                                        [command]: true,
                                    },
                                }));
                                await updateDBState();
                                setCommandType(CommandType.MAX);
                                return;
                            }
                        }

                        // If the operation is set to max
                        // Check threshold and return success if the power is bigger than the threshold
                        // Else we wait for the timer to finish and if for the whole
                        // Period the power did not exceed the threshold we finish with a failed
                        if (command === CommandType.MAX) {
                            if (
                                assetStateData.online &&
                                assetStateData.consumption >
                                    CONSUMPTION_TEST_MAX_THRESHOLD
                            ) {
                                stopPolling();
                                setTestsPassed((testPassed) => ({
                                    ...testPassed,
                                    [homeAssetId]: {
                                        ...testPassed[homeAssetId],
                                        [command]: true,
                                    },
                                }));
                                await updateDBState();
                                setCommandType(CommandType.MIN);
                                return;
                            }
                        }

                        if (command === CommandType.SELF) {
                            const result =
                                assetStateData.lastEvents.lastSelfTestResult
                                    .result;
                            const timestamp =
                                assetStateData.lastEvents.lastSelfTestResult
                                    .timestamp;
                            if (
                                timestamp >=
                                    selfTestStartTimestampRef.current &&
                                (result ===
                                    SelfTestResult.SELF_TEST_RESULT_ERR_SWITCH ||
                                    result ===
                                        SelfTestResult.SELF_TEST_RESULT_ERR_KEEP)
                            ) {
                                stopPolling();
                                setTestsFailed((testsFailed) => ({
                                    ...testsFailed,
                                    [command]: true,
                                }));
                            }
                        } else {
                            setTickCount((value) => {
                                if (value >= POLLING_MAX_TICK_COUNT) {
                                    stopPolling();
                                    setTestsFailed((testsFailed) => ({
                                        ...testsFailed,
                                        [command]: true,
                                    }));
                                    setCommandType(
                                        command === CommandType.MAX
                                            ? CommandType.MIN
                                            : CommandType.MAX
                                    );
                                }
                                return value + 1;
                            });
                        }
                    } catch (e) {
                        stopPolling();
                        setTestsFailed((testsFailed) => ({
                            ...testsFailed,
                            [command]: true,
                        }));
                        presentToast({
                            message: t`Oops, something went wrong while running the test. Please try again.`,
                            duration: 5000,
                            color: 'danger',
                            buttons: [
                                {
                                    text: t`Dismiss`,
                                    role: 'cancel',
                                },
                            ],
                        });
                    }

                    if (timeoutId.current) {
                        startTestPolling(command);
                    }
                },
                command === CommandType.SELF
                    ? POLLING_SELF_TEST_TIMEOUT
                    : POLLING_TIMEOUT
            );
        },
        [
            accessToken,
            homeAssetId,
            jobId,
            presentToast,
            setTestsPassed,
            stopPolling,
            updateDBState,
        ]
    );

    const onResetTests = useCallback(async () => {
        stopPolling();
        setShowTestDetails(false);
        // Reseting only testsPassed for current homeAssetId
        delete testsPassed[homeAssetId];
        setTestsPassed({ ...testsPassed });
        // Updating DB State removing any passedTests
        await updateDBState();
        setTestsFailed({});
        setTestInProgress(false);
        setAssetStateData(DEFAULT_ASSET_STATE);
        setCommandType(CommandType.MAX);
        trackActionsRequest('retry_test');
    }, [homeAssetId, setTestsPassed, stopPolling, testsPassed, updateDBState]);

    const startTest = useCallback(
        async (command: CommandType) => {
            const temperature =
                command === CommandType.MIN
                    ? MIN_TEMP
                    : command === CommandType.MAX
                      ? MAX_TEMP
                      : 0;
            setCommandType(command);
            setTemperatureValue(temperature / 100);
            setTickCount(1);
            setShowTestDetails(true);
            setTestInProgress(true);
            try {
                if (command === CommandType.SELF) {
                    await triggerRequest([device], {
                        selfTest: true,
                    });
                    await triggerRequest([device], {
                        eventSelfTest: true,
                    });
                } else {
                    await setTargetTemperature(temperature);
                }
                startTestPolling(command);
            } catch (e) {
                setTestsFailed((testsFailed) => ({
                    ...testsFailed,
                    [command]: true,
                }));
                presentToast({
                    message: t`Oops, something went wrong while running the test. Please try again.`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
                await onResetTests();
            }
        },
        [
            device,
            onResetTests,
            presentToast,
            setTargetTemperature,
            startTestPolling,
        ]
    );

    const onSetMinTemp = useCallback(() => {
        startTest(CommandType.MIN);
    }, [startTest]);

    const onSetMaxTemp = useCallback(() => {
        startTest(CommandType.MAX);
    }, [startTest]);

    const onSelfTest = useCallback(() => {
        selfTestStartTimestampRef.current = Date.now();
        startTest(CommandType.SELF);
    }, [startTest]);

    const maxNumberOfTests = useSelfTest ? 1 : 2;
    const testFinalised =
        testsPassedCount + Object.entries(testsFailed).length ===
        maxNumberOfTests;
    const allTestsPassed = testsPassedCount === maxNumberOfTests;

    const minTempButtonDisabled = useMemo(() => {
        return (
            testFinalised || commandType === CommandType.MAX || minTestPassed
        );
    }, [commandType, minTestPassed, testFinalised]);

    const maxTempButtonDisabled = useMemo(() => {
        return (
            testFinalised || commandType === CommandType.MIN || maxTestPassed
        );
    }, [commandType, maxTestPassed, testFinalised]);

    useEffect(() => {
        if (isNew && testFinalised) {
            trackActionsRequest(
                allTestsPassed ? 'test_passed' : 'test_failed',
                {
                    homeAssetId,
                }
            );
        }
    }, [
        accessToken,
        allTestsPassed,
        homeAssetId,
        jobId,
        testFinalised,
        setTestsPassed,
        isNew,
    ]);

    return (
        <>
            {useSelfTest ? (
                <IonRow>
                    {testInProgress ? (
                        <IonLabel color="info">
                            <Trans>Test in progress...</Trans>
                        </IonLabel>
                    ) : (
                        <IonCol size="auto">
                            <IonButton
                                size="small"
                                disabled={selfTestPassed || selfTestFailed}
                                onClick={onSelfTest}
                            >
                                <Trans>Start Test</Trans>
                            </IonButton>
                        </IonCol>
                    )}
                </IonRow>
            ) : (
                <IonRow>
                    {testInProgress ? (
                        <IonLabel color="info">
                            <Trans>Test in progress...</Trans>
                        </IonLabel>
                    ) : (
                        <>
                            <IonCol size="auto">
                                <IonButton
                                    size="small"
                                    disabled={minTempButtonDisabled}
                                    onClick={onSetMinTemp}
                                >
                                    <Trans>Set Min Temp.</Trans>
                                </IonButton>
                            </IonCol>
                            <IonCol size="auto">
                                <IonButton
                                    size="small"
                                    disabled={maxTempButtonDisabled}
                                    onClick={onSetMaxTemp}
                                >
                                    <Trans>Set Max Temp.</Trans>
                                </IonButton>
                            </IonCol>
                        </>
                    )}
                </IonRow>
            )}
            {(showTestDetails || testsPassedCount > 0) && (
                <>
                    {showTestDetails && (
                        <IonRow className="ion-align-items-center ion-spacer-top">
                            {assetStateData.online ? (
                                <div className="tests-passed">
                                    <Trans>Online</Trans>
                                </div>
                            ) : (
                                <div className="tests-failed">
                                    <Trans>Offline</Trans>
                                </div>
                            )}
                        </IonRow>
                    )}
                    {!!assetStateData.receivedAt && (
                        <IonRow className="ion-spacer-top">
                            <Trans>
                                Received at:{' '}
                                {new Date(
                                    assetStateData.receivedAt
                                ).toLocaleTimeString()}
                            </Trans>
                            {!assetStateData.online && (
                                <IonText
                                    color="warning"
                                    className="ion-padding-start"
                                >
                                    <b>
                                        <Trans>Status OLD</Trans>
                                    </b>
                                </IonText>
                            )}
                        </IonRow>
                    )}
                    {showTestDetails && !useSelfTest && (
                        <IonRow className="ion-spacer-top">
                            <Trans>
                                Temperature target: {temperatureValue} °C
                            </Trans>
                        </IonRow>
                    )}
                    {useSelfTest ? (
                        (selfTestPassed || selfTestFailed) && (
                            <IonRow className="ion-align-items-center ion-spacer-top">
                                {selfTestPassed && (
                                    <div className="tests-passed">
                                        <Trans>Passed</Trans>
                                    </div>
                                )}
                                {selfTestFailed && (
                                    <div className="tests-failed">
                                        <Trans>Failed</Trans>
                                    </div>
                                )}
                                <IonButton size="small" onClick={onResetTests}>
                                    <IonIcon icon={refresh} />
                                </IonButton>
                            </IonRow>
                        )
                    ) : (
                        <IonRow className="ion-align-items-center ion-spacer-top">
                            <div className="tests-passed">
                                <Trans>Passed: {testsPassedCount} / 2</Trans>
                            </div>
                            <div className="tests-failed">
                                <Trans>
                                    Failed:{' '}
                                    {Object.entries(testsFailed).length} / 2
                                </Trans>
                            </div>
                            <IonButton size="small" onClick={onResetTests}>
                                <IonIcon icon={refresh} />
                            </IonButton>
                        </IonRow>
                    )}
                    {showTestDetails && (
                        <>
                            <IonRow className="ion-spacer-top">
                                <Trans>
                                    Final measure: {assetStateData.consumption}{' '}
                                    W
                                </Trans>
                            </IonRow>
                            <IonRow className="ion-spacer-top">
                                <Trans>
                                    Cons. average over 5 min:{' '}
                                    {assetStateData.lastAverageTemperature} W
                                </Trans>
                            </IonRow>
                            <IonRow className="ion-spacer-top">
                                <Trans>
                                    t°{commandType} command sent to the D-BOX
                                </Trans>
                            </IonRow>
                            <IonRow className="ion-spacer-top">
                                <Trans>
                                    Target temperature:{' '}
                                    {assetStateData.lastTargetTemperature /
                                        100}
                                    °
                                </Trans>
                            </IonRow>
                            <IonRow className="ion-spacer-top">
                                <Trans>
                                    Average temperature:{' '}
                                    {assetStateData.lastAverageTemperature /
                                        100}
                                    °
                                </Trans>
                            </IonRow>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default AssetTest;
